import { useEffect, useMemo, useState } from 'react';
import { ApiError } from '../api/client';
import { mailingApi, type CampaignTemplate, type MailingApi, type MailingDashboard, type MailingSpace, type Sender, type SenderBody } from '../api/mailing';

type Tab = 'senders' | 'templates' | 'delivery';
const TABS: { key: Tab; label: string }[] = [
  { key: 'senders', label: 'Odesílatelé' },
  { key: 'templates', label: 'Šablony kampaní' },
  { key: 'delivery', label: 'Rozesílka' },
];
const FLAG: Record<string, string> = { CZ: '🇨🇿', SK: '🇸🇰', RO: '🇷🇴' };
const SERVICE_LABEL: Record<string, string> = { scheduler: 'Plánovač', worker: 'Odesílání', webhook: 'Statistiky (SendGrid webhook)' };

function fmtDateTime(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString('cs-CZ', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';
}

export function MailingSettingsPanel({ space, spaceLabel, countries, templatesPath, onBack }: {
  space: MailingSpace; spaceLabel: string; countries: readonly string[]; templatesPath: string; onBack: () => void;
}) {
  const api = useMemo(() => mailingApi(space), [space]);
  const [tab, setTab] = useState<Tab>(() => {
    const v = new URLSearchParams(window.location.search).get('mailing');
    return v === 'templates' || v === 'delivery' ? v : 'senders';
  });
  return (
    <>
      <button type="button" className="btn btn-sm btn-link px-0 mb-2" onClick={onBack}>Zpět na Nastavení</button>
      <h5 className="mb-3">Mailing - {spaceLabel} · konfigurace</h5>
      <div className="d-flex mb-3 border-bottom flex-wrap">
        {TABS.map((t) => (
          <button key={t.key} type="button" className="bg-transparent" onClick={() => setTab(t.key)}
            style={{ fontSize: '0.92rem', padding: '8px 16px', borderRadius: 0, cursor: 'pointer',
              color: tab === t.key ? 'var(--p1-green)' : 'rgb(38,42,46)',
              borderBottom: tab === t.key ? '2px solid var(--p1-green)' : '2px solid transparent',
              borderTop: 'none', borderLeft: 'none', borderRight: 'none' }}>
            {t.label}
          </button>
        ))}
      </div>
      {tab === 'senders' && <SendersTab api={api} countries={countries} />}
      {tab === 'templates' && <TemplatesTab api={api} templatesPath={templatesPath} />}
      {tab === 'delivery' && <DeliveryTab api={api} />}
    </>
  );
}

const EMPTY: SenderBody = { country: null, name: '', email: '', replyTo: null, isDefault: false, active: true, notes: null };

function SenderCard({ s, onEdit }: { s: Sender; onEdit: () => void }) {
  return (
    <div className="border rounded p-2 mb-2" style={{ fontSize: '0.82rem', opacity: s.active ? 1 : 0.55 }}>
      <div className="d-flex align-items-center gap-2 flex-wrap">
        <strong>{s.name}</strong>
        {s.isDefault && <span className="badge bg-primary-subtle text-primary" style={{ fontSize: '.58rem' }}>DEFAULT</span>}
        {!s.active && <span className="badge bg-light text-muted border" style={{ fontSize: '.58rem' }}>NEAKTIVNÍ</span>}
        <span className="ms-auto" style={{ fontSize: '.66rem', color: s.verified ? '#2ca84b' : '#c82b23' }} title={s.verified ? 'Doména ověřená v SendGridu (DKIM/SPF)' : 'Doména není v SendGridu ověřená'}>{s.verified ? '✓ DKIM/SPF' : '✗ neověřeno'}</span>
      </div>
      <div className="text-muted">{s.email}</div>
      {s.replyTo && <div className="text-muted" style={{ fontSize: '.72rem' }}>Odpověď na: {s.replyTo}</div>}
      <div className="d-flex align-items-center gap-2 mt-1">
        {s.notes && <span className="text-muted" style={{ fontSize: '.7rem' }}>{s.notes}</span>}
        <button type="button" className="btn btn-sm btn-link p-0 ms-auto" style={{ fontSize: '.74rem' }} onClick={onEdit}>Upravit</button>
      </div>
    </div>
  );
}

function SendersTab({ api, countries }: { api: MailingApi; countries: readonly string[] }) {
  const [data, setData] = useState<Sender[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ id: number | null; body: SenderBody } | null>(null);
  const [saving, setSaving] = useState(false);
  const [formErr, setFormErr] = useState<string | null>(null);

  function load() {
    api.senders().then(setData).catch((e: unknown) => setError(e instanceof ApiError ? e.message : 'Načtení selhalo'));
  }
  useEffect(load, [api]);

  async function save() {
    if (!editing) return;
    setSaving(true); setFormErr(null);
    try {
      if (editing.id == null) await api.createSender(editing.body);
      else await api.updateSender(editing.id, editing.body);
      setEditing(null);
      load();
    } catch (e) {
      setFormErr(e instanceof ApiError ? e.message : 'Uložení selhalo');
    } finally {
      setSaving(false);
    }
  }

  if (!data) return error ? <div className="alert alert-danger">{error}</div> : <div className="text-muted py-3">Načítám…</div>;
  const groups = [{ c: null as string | null, label: 'Všechny trhy' }, ...countries.map((c) => ({ c, label: `${FLAG[c] ?? ''} ${c}` }))];
  const set = (patch: Partial<SenderBody>) => setEditing((e) => (e ? { ...e, body: { ...e.body, ...patch } } : e));

  return (
    <>
      <div className="d-flex gap-2 mb-3">
        <span className="text-muted small align-self-center">Odesílatel musí mít doménu ověřenou v SendGridu, jinak maily padají do spamu.</span>
        <button type="button" className="btn btn-sm btn-primary ms-auto" onClick={() => { setFormErr(null); setEditing({ id: null, body: { ...EMPTY } }); }}>+ Nový odesílatel</button>
      </div>
      {editing && (
        <div className="card shadow-sm mb-3"><div className="card-body">
          <div className="fw-semibold mb-2">{editing.id == null ? 'Nový odesílatel' : 'Upravit odesílatele'}</div>
          {formErr && <div className="alert alert-danger py-2">{formErr}</div>}
          <div className="row g-2">
            <div className="col-sm-4"><label className="form-label small text-muted mb-1">Jméno odesílatele *</label><input className="form-control form-control-sm" value={editing.body.name} onChange={(e) => set({ name: e.target.value })} placeholder="Affilmaster" /></div>
            <div className="col-sm-4"><label className="form-label small text-muted mb-1">E-mail odesílatele *</label><input className="form-control form-control-sm" value={editing.body.email} onChange={(e) => set({ email: e.target.value })} placeholder="info@domena.cz" /></div>
            <div className="col-sm-4"><label className="form-label small text-muted mb-1">Odpověď na</label><input className="form-control form-control-sm" value={editing.body.replyTo ?? ''} onChange={(e) => set({ replyTo: e.target.value || null })} placeholder="support@domena.cz" /></div>
            <div className="col-sm-4"><label className="form-label small text-muted mb-1">Trh</label>
              <select className="form-select form-select-sm" value={editing.body.country ?? ''} onChange={(e) => set({ country: e.target.value || null })}>
                <option value="">Všechny trhy</option>{countries.map((c) => <option key={c} value={c}>{c}</option>)}
              </select></div>
            <div className="col-sm-8"><label className="form-label small text-muted mb-1">Poznámka</label><input className="form-control form-control-sm" value={editing.body.notes ?? ''} onChange={(e) => set({ notes: e.target.value || null })} /></div>
            <div className="col-12 d-flex gap-3">
              <label className="form-check form-switch mb-0"><input className="form-check-input" type="checkbox" checked={editing.body.isDefault} onChange={(e) => set({ isDefault: e.target.checked })} /> <span className="small">Výchozí odesílatel</span></label>
              <label className="form-check form-switch mb-0"><input className="form-check-input" type="checkbox" checked={editing.body.active} onChange={(e) => set({ active: e.target.checked })} /> <span className="small">Aktivní</span></label>
            </div>
          </div>
          <div className="d-flex gap-2 justify-content-end mt-3">
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setEditing(null)} disabled={saving}>Zrušit</button>
            <button type="button" className="btn btn-sm btn-primary" onClick={save} disabled={saving}>{saving ? 'Ukládám…' : 'Uložit'}</button>
          </div>
        </div></div>
      )}
      <div className="row g-3">
        {groups.map(({ c, label }) => {
          const items = data.filter((s) => (s.country ?? null) === c);
          return (
            <div className="col-md-4" key={label}>
              <div className="card shadow-sm h-100"><div className="card-body">
                <div className="fw-semibold mb-3">{label} <span className="text-muted small">· {items.length} odesílatelů</span></div>
                {items.length
                  ? items.map((s) => <SenderCard key={s.id} s={s} onEdit={() => { setFormErr(null); setEditing({ id: s.id, body: { country: s.country, name: s.name, email: s.email, replyTo: s.replyTo, isDefault: s.isDefault, active: s.active, notes: s.notes } }); }} />)
                  : <div className="text-muted fst-italic" style={{ fontSize: '.78rem', padding: '4px 2px' }}>Žádný odesílatel</div>}
              </div></div>
            </div>
          );
        })}
      </div>
    </>
  );
}

function TemplatesTab({ api, templatesPath }: { api: MailingApi; templatesPath: string }) {
  const [data, setData] = useState<CampaignTemplate[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    api.templates({ all: true }).then(setData).catch((e: unknown) => setError(e instanceof ApiError ? e.message : 'Načtení selhalo'));
  }, [api]);
  if (!data) return error ? <div className="alert alert-danger">{error}</div> : <div className="text-muted py-3">Načítám…</div>;
  return (
    <>
      <div className="d-flex mb-2"><a className="btn btn-sm btn-primary ms-auto" href={templatesPath}>Otevřít knihovnu šablon</a></div>
      <div className="table-responsive-mobile">
        <table className="table table-sm align-middle" style={{ fontSize: '0.82rem' }}>
          <thead><tr className="text-muted"><th>Název</th><th>Jazyk</th><th>Trh</th><th>Předmět</th><th>Stav</th><th>Upraveno</th></tr></thead>
          <tbody>
            {data.map((t) => (
              <tr key={t.id}>
                <td><a href={`${templatesPath}?edit=${t.id}`}>{t.name}</a></td>
                <td><code>{t.lang}</code></td>
                <td>{t.country ? `${FLAG[t.country] ?? ''} ${t.country}` : 'Všechny'}</td>
                <td className="text-muted">{t.subject ?? '-'}</td>
                <td>{t.enabled ? <span className="badge bg-success-subtle text-success">aktivní</span> : <span className="badge bg-light text-muted border">archiv</span>}</td>
                <td className="text-muted">{fmtDateTime(t.updatedAt)}</td>
              </tr>
            ))}
            {data.length === 0 && <tr><td colSpan={6} className="text-muted text-center py-3">Žádné šablony.</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}

function DeliveryTab({ api }: { api: MailingApi }) {
  const [d, setD] = useState<MailingDashboard | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { api.dashboard().then(setD).catch((e: unknown) => setError(e instanceof ApiError ? e.message : 'Načtení selhalo')); }, [api]);
  if (!d) return error ? <div className="alert alert-danger">{error}</div> : <div className="text-muted py-3">Načítám…</div>;
  const row = (label: string, value: React.ReactNode) => <tr><td className="text-muted" style={{ width: 280 }}>{label}</td><td>{value}</td></tr>;
  return (
    <div className="card shadow-sm"><div className="card-body">
      <table className="table table-sm mb-0" style={{ fontSize: '0.86rem' }}>
        <tbody>
          {row('Poskytovatel', 'SendGrid')}
          {row('Napojení', d.sendgridConfigured ? <span className="text-success">✓ nastaveno</span> : <span className="text-danger">✗ chybí API klíč nebo odesílatel</span>)}
          {row('Režim rozesílky', d.sendLive ? <span className="text-success">Ostrý provoz</span> : <span className="text-warning">Testovací (maily se reálně neodesílají)</span>)}
          {row('Krátké odkazy (měření prokliků)', d.shortHost ? <span className="text-success">✓ {d.shortHost}</span> : <span className="text-muted">nenastaveno, prokliky měří SendGrid</span>)}
          {row('Odhlášené kontakty', d.unsubscribed.toLocaleString('cs-CZ'))}
          {row('Fronta', `${d.queue.pending} čeká · ${d.queue.processing} se odesílá · ${d.queue.failed} chyb`)}
          {row('Odesláno 7 / 30 dní', `${d.stats.sent7d.toLocaleString('cs-CZ')} / ${d.stats.sent30d.toLocaleString('cs-CZ')}`)}
          {d.services.map((s) => row(SERVICE_LABEL[s.key] ?? s.key, <>{s.status === 'ok' ? <span className="text-success">OK</span> : s.status === 'fail' ? <span className="text-danger">Chyba</span> : <span className="text-muted">zatím neběželo</span>} <span className="text-muted small">· poslední běh {fmtDateTime(s.lastRunAt)}</span>{s.lastError ? <div className="text-danger small">{s.lastError}</div> : null}</>))}
        </tbody>
      </table>
    </div></div>
  );
}
